import React, { useState, useEffect } from "react";
import { Autocomplete, Button, Dialog, Grow, Popover, TextField, Tooltip } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import FlagRoundedIcon from "@mui/icons-material/FlagRounded";
import NotesRoundedIcon from "@mui/icons-material/NotesRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import axios from "axios";
import API_BASE_URL from "../../components/config/appConfig";
import { issueStatus, IssueTypes } from "../../utilities/Json";
import { useLocation } from "react-router-dom";
import CKTextEditor from "../CK-Text-Editor";
import { Formik, Form, ErrorMessage, useFormikContext } from "formik";
import { backlogissueSchema } from "../../Validations/schema";
import { getSprintsList, getBacklogIssues, setSelectedProjectData, clearSprintsListAndActiveSprintData } from "../../common/Redux/Reducer";
import SearchAutoComplete from "../SearchAutoComplete";
import { useDispatch, useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { Glyph, getStatusMeta, getTypeMeta } from "../Modal/TaskWorkFlowParts";
import { useToast } from "../Toast";
import "./createIssue.scss";

/* --------------------------------------------------------------------------
   Create Issue dialog — the active sprint board's design language: a light
   header with the brand hairline, bordered cards instead of shadows, pill
   controls and green gradient actions. Styles live in createIssue.scss.
   -------------------------------------------------------------------------- */

const SmoothGrow = React.forwardRef(function SmoothGrow(props, ref) {
  return <Grow ref={ref} timeout={{ enter: 320, exit: 200 }} {...props} />;
});

/* SelectMenuTextField's value contract: `_id` when present, else name/title/role */
const optionValue = (option) =>
  option?._id ? option._id : option?.name ? option.name : option?.title ? option.title : option?.role || "";

const optionLabel = (option) => (option?.name ? option.name : option?.title ? option.title : option?.role || "");

/**
 * Whoever opens this dialog is the reporter, so the field starts on them and
 * stays editable. Runs once the reporter list has loaded and only while the
 * field is untouched, so an explicit choice is never overwritten.
 */
const DefaultReporterToCurrentUser = ({ reporters = [], currentUserId, currentUserEmail }) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (values.reporter || !reporters.length) return;
    const me = reporters.find((r) => r?._id === currentUserId || (currentUserEmail && r?.email === currentUserEmail));
    if (me?._id) setFieldValue("reporter", me._id);
  }, [reporters, values.reporter, currentUserId, currentUserEmail, setFieldValue]);

  return null;
};

/**
 * The project picker is driven from redux, so a project selected before this
 * dialog opened never reached Formik and failed the required-field check.
 * Fills it in only while blank, so an explicit change is never overwritten.
 */
const SyncSelectedProjectToForm = ({ selectedProjectData }) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (values.project || !selectedProjectData?._id) return;
    setFieldValue("project", selectedProjectData._id);
    if (!values.backlog && selectedProjectData?.backlogs?.[0]) {
      setFieldValue("backlog", selectedProjectData.backlogs[0]);
    }
  }, [selectedProjectData, values.project, values.backlog, setFieldValue]);

  return null;
};

/* Searchable select that re-emits `{ target: { value } }`, so Formik's
   handleChange works unchanged. Controlled by `value` alone. */
const MaterialSelect = ({ labelText, options = [], extraOptions = [], value = "", onChange = () => {}, onBlur = () => {}, isDisable = false, required = false }) => {
  const allOptions = [...extraOptions, ...(options || [])].filter((option) => optionValue(option));
  const selected = allOptions.find((option) => optionValue(option) === value) || null;

  return (
    <Autocomplete
      className="pj-ci-select"
      options={allOptions}
      value={selected}
      onChange={(event, newValue) => onChange({ target: { value: newValue ? optionValue(newValue) : "" } })}
      onBlur={onBlur}
      disabled={isDisable}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={(option, val) => optionValue(option) === optionValue(val)}
      fullWidth
      size="small"
      openOnFocus
      popupIcon={<ExpandMoreRoundedIcon />}
      noOptionsText={`No ${labelText?.toLowerCase()} found`}
      slotProps={{ paper: { className: "pj-ci-pop" } }}
      renderOption={(props, option, { selected: isSelected }) => {
        const { key, ...rowProps } = props;
        return (
          <li key={key} {...rowProps}>
            <span className="pj-ci-pop__label">{optionLabel(option)}</span>
            {isSelected ? <CheckRoundedIcon className="pj-ci-pop__check" /> : null}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={
            <>
              {labelText}
              {required ? <span className="pj-ci-req">*</span> : null}
            </>
          }
          variant="outlined"
        />
      )}
    />
  );
};

/* Plain-language guide behind the issue-type "Guide" link, in IssueTypes order */
const issueTypeGuide = [
  {
    name: "Task",
    summary: "A single piece of work to be done.",
    detail:
      "Use it for anything that isn't user-facing behaviour or a defect — setup, research, a chore, a one-off change. Tasks are the default and should be small enough to finish inside a sprint.",
  },
  {
    name: "Story",
    summary: "A feature described from the user's point of view.",
    detail:
      "Written as something a user can do once it ships. Stories are the main unit of delivery on a sprint board and usually sit under an epic.",
  },
  {
    name: "Bug",
    summary: "Something that works differently from how it should.",
    detail:
      "A defect in behaviour that already exists. Include what you expected, what actually happened, and the steps to reproduce it.",
  },
  {
    name: "Epic",
    summary: "A large body of work spanning several sprints.",
    detail:
      "A container, not a unit of work. Break an epic down into the stories and tasks that deliver it, and track progress through its children.",
  },
];

const CreateBacklogIssue = ({ isCreateNewBackIssuse, setIsCreateNewBackIssue, sprint = "" }) => {
  const [open, setOpen] = React.useState(isCreateNewBackIssuse);
  const [reporters, setReporters] = useState([]);
  const [SelectedEmp, setSelectedEmp] = useState();
  const [members, setMembers] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("To Do");
  const [disabledBtn, setDisabledBtn] = useState(false);
  const [issueTypeInfoAnchor, setIssueTypeInfoAnchor] = useState(null);
  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [newDescription, setNewDescription] = useState("");

  const location = useLocation();
  const dispatch = useDispatch();
  const toast = useToast();
  const filterAssigneesArray = useSelector((state) => state.ProjamAppSlice.assignee);

  const { Projects } = useSelector((state) => state.ProjamAppSlice);
  const allProjectsList = Projects?.allProjectsList;
  const activeSprint = Projects?.selectedProject?.ActiveSprint;
  const sprintsList = Projects?.selectedProject?.sprintsList;
  const selectedProjectData = Projects?.selectedProject?.selectedProjectData;
  const filteredProjects = allProjectsList?.data?.filter((project) => project.active === "true");
  const projectOptions = filteredProjects || [];
  const isOnProjectsPage = location?.pathname === "/projects";
  // the project is fixed everywhere except the projects landing page
  const isDisable = !isOnProjectsPage;

  const token = localStorage.getItem("accessToken");
  /* jwtDecode throws on a missing or malformed token, which took the whole
     dialog down with an uncaught error instead of just rendering without the
     current user. */
  let decoded = {};
  try {
    if (token) decoded = jwtDecode(token);
  } catch (error) {
    console.warn("Could not decode the access token", error?.message);
  }

  let backlogId = Projects?.selectedProject?.selectedProjectBackLogId;
  let projectId = Projects?.selectedProject?.selectedProjectData?._id;

  const handleClose = () => {
    setOpen(false);
    setIsCreateNewBackIssue(false);
    if (isOnProjectsPage) {
      dispatch(setSelectedProjectData(""));
      setMembers([]);
    }
  };

  useEffect(() => {
    getReporters();
    if (!isOnProjectsPage) {
      getProjectData();
    }
    // loads once for the dialog
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function fetchSprintsList() {
      if (selectedProjectData && selectedProjectData?.backlogs?.[0]) {
        await dispatch(getSprintsList({ backlogId: selectedProjectData?.backlogs[0], filterAssigneesArray: selectedProjectData?.contributors }));
        setMembers(selectedProjectData?.contributors);
      } else {
        await dispatch(clearSprintsListAndActiveSprintData());
      }
    }
    if (isOnProjectsPage) {
      fetchSprintsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectData]);

  const getReporters = async () => {
    try {
      let response = await axios.get(`${API_BASE_URL}/users/getReporters`);
      setReporters(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  const getProjectData = async () => {
    try {
      let response = await axios(`${API_BASE_URL}/projects/getProjectsById/${projectId}`);
      backlogId = response?.data?.backlogs[0];
      setMembers(response?.data?.contributors);
    } catch (error) {
      console.log(error);
    }
  };

  const options = location?.pathname?.includes("/active-sprints") ? [{ ...activeSprint }] : sprintsList;

  const handleSubmit = async (values) => {
    setDisabledBtn(true);
    const isSprintIssue = values?.sprint && values?.sprint !== "backlog";
    try {
      if (isSprintIssue) {
        const Data = { ...values, status: selectedStatus, assignee: SelectedEmp, description: newDescription };
        await axios.post(`${API_BASE_URL}/issues/createIssue?userid=${decoded?.id}`, Data);
        handleClose();
        await dispatch(getSprintsList({ backlogId: selectedProjectData?.backlogs[0], filterAssigneesArray: filterAssigneesArray }));
      } else {
        const Data = { ...values, status: selectedStatus, assignee: SelectedEmp, description: newDescription, sprint: null };
        await axios.post(`${API_BASE_URL}/issues/createBacklogIssue`, Data);
        backlogId && dispatch(getBacklogIssues(backlogId));
        handleClose();
      }
      toast.success("Issue created");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Could not create the issue. Please try again.");
    } finally {
      setDisabledBtn(false);
    }
  };

  const createIssueFormInitialValues = {
    project: !isOnProjectsPage ? projectId : (selectedProjectData && selectedProjectData?._id) || "",
    issueType: "Task",
    status: selectedStatus,
    summary: "",
    description: "",
    reporter: "",
    assignee: "",
    backlog: !isOnProjectsPage ? backlogId : (selectedProjectData && selectedProjectData?.backlogs?.[0]) || "",
    sprint: location?.pathname?.includes("/active-sprints") ? activeSprint?._id : sprint ? sprint : "backlog",
  };

  const handleChangeProject = async (event, setFieldValue) => {
    const selectedProject = projectOptions?.filter((eachProject) => eachProject?._id === event?.target?.value);
    if (selectedProject && selectedProject?.length > 0) {
      await dispatch(setSelectedProjectData(selectedProject[0]));
      setMembers(selectedProject[0]?.contributors);

      /* Keep the form in step with the picker, or `project` fails validation
         and the backlog stays pointed at the old project. */
      if (setFieldValue) {
        setFieldValue("project", selectedProject[0]?._id || "");
        setFieldValue("backlog", selectedProject[0]?.backlogs?.[0] || "");
      }
    }
  };

  const describeDestination = (sprintValue) => {
    if (!sprintValue || sprintValue === "backlog") return "Backlog";
    return (options || []).find((option) => option?._id === sprintValue)?.name || "Sprint";
  };

  return (
    <Dialog
      open={open}
      maxWidth={false}
      disableEscapeKeyDown
      TransitionComponent={SmoothGrow}
      PaperProps={{ className: "pj-ci" }}
      slotProps={{
        backdrop: {
          className: "pj-ci-backdrop",
          onClick: (event) => event.stopPropagation(),
        },
      }}
      aria-labelledby="create-issue-title"
    >
      <Formik initialValues={createIssueFormInitialValues} validationSchema={backlogissueSchema} onSubmit={handleSubmit}>
        {({ handleChange, handleBlur, setFieldValue, values }) => (
          <Form className="pj-ci-form" noValidate>
            <SyncSelectedProjectToForm selectedProjectData={selectedProjectData} />
            <DefaultReporterToCurrentUser reporters={reporters} currentUserId={decoded?.id} currentUserEmail={decoded?.email} />

            {/* ------------------------------------------------------ header */}
            <header className="pj-ci-head">
              <span className="pj-ci-tile" aria-hidden="true">
                <AddTaskRoundedIcon />
              </span>
              <div className="pj-ci-heading">
                <h2 id="create-issue-title">Create issue</h2>
                <p>
                  {selectedProjectData?.title ? (
                    <>
                      In <strong>{selectedProjectData.title}</strong>
                      <span className="pj-ci-dot" />
                      {describeDestination(values.sprint)}
                    </>
                  ) : (
                    "Pick a project to get started"
                  )}
                </p>
              </div>
              <Tooltip title="Close">
                <button type="button" className="pj-ci-close" onClick={handleClose} aria-label="Close">
                  <CloseRoundedIcon />
                </button>
              </Tooltip>
            </header>

            {/* -------------------------------------------------------- body */}
            <div className="pj-ci-body">
              <div className="pj-ci-main">
                <section className="pj-ci-card" style={{ "--d": "40ms" }}>
                  <h3 className="pj-ci-card__title">
                    <span className="pj-ci-card__icon pj-tone--blue">
                      <NotesRoundedIcon />
                    </span>
                    Issue details
                  </h3>

                  <div className="pj-ci-field">
                    <label className="pj-ci-label" htmlFor="pj-ci-summary">
                      Title<span className="pj-ci-req">*</span>
                    </label>
                    <TextField
                      id="pj-ci-summary"
                      className="pj-ci-input pj-ci-input--title"
                      fullWidth
                      autoFocus
                      placeholder="What needs to be done?"
                      value={values.summary}
                      onChange={handleChange("summary")}
                      onBlur={handleBlur("summary")}
                      inputProps={{ maxLength: 255 }}
                    />
                    <ErrorMessage name="summary" component="span" className="pj-ci-error" />
                  </div>

                  <div className="pj-ci-field">
                    <span className="pj-ci-label">Description</span>
                    {isEditingDesc ? (
                      <div className="pj-ci-editor">
                        <CKTextEditor payloadText={newDescription} onChange={setNewDescription} autoFocus />
                      </div>
                    ) : (
                      <button type="button" className="pj-ci-empty" onClick={() => setIsEditingDesc(true)}>
                        <EditNoteRoundedIcon />
                        Add a description…
                      </button>
                    )}
                  </div>
                </section>

                <section className="pj-ci-card" style={{ "--d": "110ms" }}>
                  <h3 className="pj-ci-card__title">
                    <span className="pj-ci-card__icon pj-tone--teal">
                      <PeopleAltRoundedIcon />
                    </span>
                    People
                  </h3>
                  <div className="pj-ci-grid">
                    <div className="pj-ci-field pj-ci-field--flush">
                      <SearchAutoComplete labelName="Assignee" data={members || []} setSelectedEmp={setSelectedEmp} isCreateIssuePage={true} />
                      <ErrorMessage name="assignee" component="span" className="pj-ci-error" />
                    </div>
                    <div className="pj-ci-field pj-ci-field--flush">
                      <MaterialSelect
                        labelText="Reporter"
                        options={reporters}
                        onChange={handleChange("reporter")}
                        onBlur={handleBlur("reporter")}
                        value={values.reporter}
                      />
                      <ErrorMessage name="reporter" component="span" className="pj-ci-error" />
                    </div>
                  </div>
                </section>
              </div>

              <aside className="pj-ci-side">
                <section className="pj-ci-card" style={{ "--d": "70ms" }}>
                  <h3 className="pj-ci-card__title">
                    <span className="pj-ci-card__icon pj-tone--green">
                      <TuneRoundedIcon />
                    </span>
                    Where it goes
                  </h3>
                  <div className="pj-ci-stack">
                    <div className="pj-ci-field pj-ci-field--flush">
                      <MaterialSelect
                        labelText="Project"
                        required
                        options={projectOptions}
                        onChange={(event) => handleChangeProject(event, setFieldValue)}
                        value={selectedProjectData?._id || ""}
                        isDisable={isDisable}
                      />
                      <ErrorMessage name="project" component="span" className="pj-ci-error" />
                    </div>
                    <div className="pj-ci-field pj-ci-field--flush">
                      <MaterialSelect
                        labelText="Sprint"
                        options={options}
                        extraOptions={[{ _id: "backlog", name: "Backlog Issues" }]}
                        onChange={handleChange("sprint")}
                        onBlur={handleBlur("sprint")}
                        value={values.sprint}
                      />
                      <ErrorMessage name="sprint" component="span" className="pj-ci-error" />
                    </div>
                  </div>
                </section>

                <section className="pj-ci-card" style={{ "--d": "130ms" }}>
                  <h3 className="pj-ci-card__title">
                    <span className="pj-ci-card__icon pj-tone--violet">
                      <CategoryRoundedIcon />
                    </span>
                    Issue type<span className="pj-ci-req">*</span>
                    <button type="button" className="pj-ci-link" onClick={(event) => setIssueTypeInfoAnchor(event.currentTarget)}>
                      <InfoOutlinedIcon />
                      Guide
                    </button>
                  </h3>
                  <div className="pj-ci-types" role="radiogroup" aria-label="Issue type">
                    {IssueTypes.map((type) => {
                      const meta = getTypeMeta(type.name);
                      const isSelected = values.issueType === type.value;
                      return (
                        <button
                          key={type.value}
                          type="button"
                          role="radio"
                          aria-checked={isSelected}
                          className={`pj-ci-choice pj-tone--${meta.tone} ${isSelected ? "is-selected" : ""}`}
                          onClick={() => setFieldValue("issueType", type.value)}
                        >
                          <Glyph meta={meta} key={isSelected ? "on" : "off"} />
                          <span className="pj-ci-choice__label">{type.name}</span>
                          {isSelected ? <CheckRoundedIcon className="pj-ci-choice__check" /> : null}
                        </button>
                      );
                    })}
                  </div>
                  <ErrorMessage name="issueType" component="span" className="pj-ci-error" />

                  <Popover
                    open={Boolean(issueTypeInfoAnchor)}
                    anchorEl={issueTypeInfoAnchor}
                    onClose={() => setIssueTypeInfoAnchor(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    slotProps={{ paper: { className: "pj-ci-guide" } }}
                  >
                    <div className="pj-ci-guide__head">
                      <InfoOutlinedIcon />
                      Issue types
                    </div>
                    <div className="pj-ci-guide__list">
                      {issueTypeGuide.map((type) => {
                        const meta = getTypeMeta(type.name);
                        return (
                          <div className={`pj-ci-guide__row pj-tone--${meta.tone}`} key={type.name}>
                            <span className="pj-ci-guide__icon">
                              <Glyph meta={meta} />
                            </span>
                            <div>
                              <div className="pj-ci-guide__name">
                                <strong>{type.name}</strong>
                                <span>{type.summary}</span>
                              </div>
                              <p>{type.detail}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Popover>
                </section>

                <section className="pj-ci-card" style={{ "--d": "190ms" }}>
                  <h3 className="pj-ci-card__title">
                    <span className="pj-ci-card__icon pj-tone--amber">
                      <FlagRoundedIcon />
                    </span>
                    Status
                  </h3>
                  <div className="pj-ci-statuses" role="radiogroup" aria-label="Initial status">
                    {issueStatus.map((status) => {
                      const meta = getStatusMeta(status.name);
                      const isSelected = selectedStatus === status.name;
                      return (
                        <button
                          key={status.name}
                          type="button"
                          role="radio"
                          aria-checked={isSelected}
                          className={`pj-ci-choice pj-ci-choice--status pj-tone--${meta.tone} ${isSelected ? "is-selected" : ""}`}
                          onClick={() => setSelectedStatus(status.name)}
                        >
                          <Glyph meta={meta} key={isSelected ? "on" : "off"} />
                          <span className="pj-ci-choice__label">{status.name}</span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="pj-ci-note">The status the issue starts in.</p>
                </section>
              </aside>
            </div>

            {/* ------------------------------------------------------ footer */}
            <footer className="pj-ci-foot">
              <span className="pj-ci-hint">
                <span className="pj-ci-req">*</span> Required
              </span>
              <Button type="button" className="pj-ci-btn pj-ci-btn--ghost" onClick={handleClose} disabled={disabledBtn} disableRipple>
                Cancel
              </Button>
              <Button
                type="submit"
                className="pj-ci-btn pj-ci-btn--primary"
                disabled={disabledBtn}
                disableRipple
                startIcon={disabledBtn ? <span className="pj-ci-spinner" /> : <AddRoundedIcon />}
              >
                {disabledBtn ? "Creating…" : "Create issue"}
              </Button>
            </footer>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default CreateBacklogIssue;
